import { useRef, type ElementType, type KeyboardEvent, type ReactNode } from "react";

import { cn } from "../lib/cn";

export interface RadioCardOption<Value extends string> {
  value: Value;
  label: string;
  /** One or two short lines: what choosing this option means. */
  description?: ReactNode;
  icon?: ElementType;
  /** Quiet trailing metadata on the label line, e.g. a scope or a count. */
  meta?: ReactNode;
  disabled?: boolean;
}

export interface RadioCardsProps<Value extends string> {
  /** Accessible name of the group. */
  label: string;
  /** `""` when nothing is chosen yet: the first enabled card is then the tab stop. */
  value: Value | "";
  options: readonly RadioCardOption<Value>[];
  onChange: (value: Value) => void;
  /** Cards per row from the `sm` breakpoint; one per row below it. */
  columns?: 1 | 2 | 3;
  size?: "sm" | "md";
  /** `stacked` uses roomier spacing for a few big choices. */
  layout?: "inline" | "stacked";
  className?: string;
}

const COLUMN_CLASS = { 1: "", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3" } as const;

/**
 * A single choice drawn as cards: a leading radio marker, one label and one short
 * description each. The shared pattern for choosing a starting point, a
 * template or a mode inside a form. It behaves as a radio group (one tab stop,
 * arrow keys move and select), so pages never re-implement the semantics.
 */
export function RadioCards<Value extends string>({
  label,
  value,
  options,
  onChange,
  columns = 2,
  size = "md",
  layout = "inline",
  className,
}: RadioCardsProps<Value>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const enabled = options
    .map((option, index) => (option.disabled ? -1 : index))
    .filter((index) => index >= 0);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const tabStop = selectedIndex >= 0 ? selectedIndex : (enabled[0] ?? -1);

  function move(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (!step || enabled.length === 0) return;
    event.preventDefault();
    const position = enabled.indexOf(index);
    const next = enabled[(position + step + enabled.length) % enabled.length];
    onChange(options[next].value);
    refs.current[next]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn("grid grid-cols-1 gap-2", COLUMN_CLASS[columns], className)}
    >
      {options.map((option, index) => {
        const selected = index === selectedIndex;
        return (
          <button
            key={option.value}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={option.disabled}
            tabIndex={index === tabStop ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => move(event, index)}
            className={cn(
              "group/card flex rounded-control border bg-surface text-left transition-colors",
              "items-start gap-3",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              "disabled:cursor-not-allowed disabled:opacity-50",
              layout === "stacked" ? "px-4 py-4" : size === "sm" ? "px-3 py-2.5" : "px-3.5 py-3",
              selected
                ? "border-interaction-text bg-focus/[0.06] shadow-[inset_0_0_0_1px_var(--interaction-text)] dark:bg-focus/10"
                : "border-control-border hover:border-control-border-hover",
            )}
          >
            {/* A drawn radio, as on a native control: an empty ring, or a ring with a solid centre. */}
            <span
              aria-hidden
              className={cn(
                "mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border transition-colors",
                selected
                  ? "border-interaction-text bg-surface"
                  : "border-control-border-hover bg-surface group-hover/card:border-muted",
              )}
            >
              <span
                className={cn(
                  "size-2 rounded-full bg-interaction-text transition-transform motion-reduce:transition-none",
                  selected ? "scale-100" : "scale-0",
                )}
              />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="min-w-0 flex-1 text-sm font-medium text-primary">
                  {option.label}
                </span>
                {option.meta ? (
                  <span className="shrink-0 text-xs text-muted">{option.meta}</span>
                ) : null}
              </span>
              {option.description ? (
                <span
                  className={cn(
                    "block text-xs leading-5 text-muted",
                    layout === "stacked" ? "mt-1.5" : "mt-0.5",
                  )}
                >
                  {option.description}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
